import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ShieldAlert, FileWarning, RefreshCw, CalendarX, Building2, Coins, UserX } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "senza_compagnia", label: "Polizze senza compagnia", icon: Building2, tone: "rose" },
  { key: "senza_scadenza", label: "Polizze senza data scadenza", icon: CalendarX, tone: "amber" },
  { key: "attive_scadute", label: "Polizze attive ma scadute", icon: AlertTriangle, tone: "red" },
  { key: "cf_intestatario_diverso", label: "CF intestatario diverso dal cliente", icon: UserX, tone: "rose" },
  { key: "senza_imponibile", label: "Polizze senza imponibile", icon: FileWarning, tone: "indigo" },
  { key: "senza_premio", label: "Polizze attive senza premio", icon: Coins, tone: "amber" },
  { key: "senza_provvigione", label: "Polizze attive senza provvigione", icon: Coins, tone: "indigo" },
];

const TONE_MAP = {
  rose: "border-rose-300 bg-rose-50 text-rose-800",
  amber: "border-amber-300 bg-amber-50 text-amber-800",
  red: "border-red-400 bg-red-50 text-red-900",
  indigo: "border-indigo-300 bg-indigo-50 text-indigo-800",
};

export default function PolicyQualityReport() {
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCategory, setOpenCategory] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/policies/quality-issues");
      setReport(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="quality-report-page">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-rose-600 mb-2">Qualità dati</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
            <ShieldAlert className="h-8 w-8 sm:h-10 sm:w-10 text-rose-600 bg-rose-100 rounded-lg p-1.5" />
            Polizze sospette
          </h1>
          <p className="text-slate-500 mt-1">Anomalie e incongruenze rilevate sulle polizze salvate.</p>
        </div>
        <Button data-testid="quality-refresh" variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </Button>
      </header>

      {loading && <p className="text-slate-400">Caricamento…</p>}

      {report && !loading && (
        <>
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-lg p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-300">Riepilogo</p>
              <p className="font-display text-3xl font-bold tabular-nums mt-1" data-testid="quality-total-issues">{report.total_issues} anomalie</p>
              <p className="text-xs text-slate-400 mt-0.5">su {report.policies_scanned} polizze analizzate</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-wider text-slate-300">Tasso integrità</p>
              <p className="font-display text-3xl font-bold text-emerald-400 tabular-nums">
                {report.policies_scanned > 0 ? Math.max(0, Math.round(100 - (report.total_issues / report.policies_scanned) * 100 / 7)) : 100}%
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {CATEGORIES.map((cat) => {
              const count = report.counts?.[cat.key] || 0;
              const items = report.issues?.[cat.key] || [];
              const Icon = cat.icon;
              const isOpen = openCategory === cat.key;
              return (
                <div
                  key={cat.key}
                  data-testid={`quality-cat-${cat.key}`}
                  className={`border-l-4 ${TONE_MAP[cat.tone]} rounded-md bg-white border border-slate-200 transition-shadow ${count > 0 ? "hover:shadow-md cursor-pointer" : "opacity-60"}`}
                  onClick={() => count > 0 && setOpenCategory(isOpen ? null : cat.key)}
                >
                  <div className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon className="h-5 w-5 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{cat.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{count > 0 ? "Clicca per vedere l'elenco" : "Nessuna anomalia"}</p>
                      </div>
                    </div>
                    <span className="font-display text-2xl font-bold tabular-nums shrink-0">{count}</span>
                  </div>
                  {isOpen && count > 0 && (
                    <div className="border-t border-slate-200 bg-slate-50 p-3 max-h-80 overflow-y-auto" data-testid={`quality-items-${cat.key}`}>
                      <ul className="space-y-1.5">
                        {items.map((it) => (
                          <li key={it.id} className="text-xs flex items-center justify-between gap-2 bg-white border border-slate-200 rounded px-2 py-1.5 hover:bg-slate-50">
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate">{it.policy_number}</p>
                              <p className="text-slate-500 truncate">{it.client_name} · {it.type}{it.end_date ? ` · scad. ${formatDate(it.end_date)}` : ""}{it.premium ? ` · ${formatCurrency(it.premium)}` : ""}</p>
                              {it.policy_cf && it.client_cf && (
                                <p className="text-[10px] text-rose-700 mt-0.5 font-mono">PDF: {it.policy_cf} · Cliente: {it.client_cf}</p>
                              )}
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); nav(`/policies?open=${it.id}`); }}
                              className="text-blue-600 hover:text-blue-800 text-xs underline shrink-0"
                              data-testid={`quality-open-${it.id}`}
                            >
                              Apri
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
